import React from 'react';

export default function PrivacyPolicy() {
    return (
        <div className="max-w-4xl mx-auto p-8 text-slate-800">
            <header className="flex items-center gap-4 mb-6">
                <div className="bg-white p-2 rounded-lg shadow">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2b6cb0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2v6"></path>
                        <path d="M6 10l6 12 6-12"></path>
                    </svg>
                </div>
                <div>
                    <h1 className="text-2xl font-semibold">Privacy Policy — Legsplorer</h1>
                    <p className="text-sm text-slate-500">Effective date: <strong>November 21, 2025</strong></p>
                </div>
            </header>

            <div className="bg-white p-6 rounded-2xl shadow">
                <p>
                    Thank you for using <strong>Legsplorer</strong> ("we", "us", or "our"). Legsplorer is a mobile application that helps users generate themed city walks based on distance and interests. This Privacy Policy explains how we handle information when you use the app. By using Legsplorer you agree to the terms described below.
                </p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">1. Summary</h2>
                <p className="text-slate-600 text-sm">
                    Short version: Legsplorer collects minimal data. To support user features such as walk ratings, a <strong>random device identifier (UUID)</strong> may be generated. This UUID is stored locally on your device and may also be sent to our backend (Supabase) for calculating average walk ratings. The UUID is not linked to personal identity. No account, login, or personal information is required. Additionally, we display ads via Google AdMob which may use your device’s Advertising ID in accordance with Google’s policies.
                </p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">2. Information We Collect</h2>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Randomly generated <strong>device identifier (UUID)</strong> used to track which walks you have rated.</li>
                    <li>User preferences and selections stored locally, including saved walks, chosen themes, and distance ranges.</li>
                    <li>Minimal contextual data sent to the backend for generating walks, such as city name, theme, and walk coordinates.</li>
                    <li><strong>Advertising ID:</strong> Legsplorer and AdMob may use your device's Advertising ID (GAID) to serve ads and measure ad performance. This ID is anonymous and not linked to your personal identity.</li>
                </ul>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">3. Local Device Storage</h2>
                <p>The app stores non-personal data locally on your device, including:</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Saved or favorite walks</li>
                    <li>User-selected preferences such as chosen theme and distance</li>
                    <li>Device identifier (UUID) used only for tracking walk ratings</li>
                </ul>
                <p>All such data remains on your device and is removed when you uninstall the app or clear its app storage.</p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">4. Backend Proxy & Data Flow</h2>
                <p>
                    Walk requests are sent to our backend server, which acts as a proxy to third-party services. Minimal contextual data (city, theme, distance, and coordinates) is forwarded. If you submit a walk rating, the UUID may also be sent to Supabase to store ratings and calculate averages.
                </p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>No personal identifiers (name, email, phone) are logged.</li>
                    <li>All communication uses HTTPS encryption.</li>
                    <li>UUIDs are used only for rating tracking; they are not linked to personal identity.</li>
                </ul>

                <div className="bg-blue-50 border-l-4 border-blue-300 p-4 rounded-md mt-4">
                    <strong>Note:</strong> Storing UUIDs in Supabase is solely for tracking walk ratings and calculating averages. They are never used for profiling or marketing.
                </div>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">5. Third‑Party Services</h2>
                <table className="w-full border border-slate-200 text-sm mt-2">
                    <thead>
                    <tr className="bg-slate-100">
                        <th className="p-2 text-left">Service</th>
                        <th className="p-2 text-left">Purpose</th>
                        <th className="p-2 text-left">Example Data Sent</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr><td className="p-2">OpenAI API</td><td className="p-2">Generate themed walk descriptions</td><td className="p-2">City name, theme keywords, distance</td></tr>
                    <tr><td className="p-2">Google Maps Places API</td><td className="p-2">Place & city suggestions</td><td className="p-2">Search keywords</td></tr>
                    <tr><td className="p-2">Google Maps Directions API</td><td className="p-2">Route creation and distance calculation</td><td className="p-2">Coordinates of selected stops</td></tr>
                    <tr><td className="p-2">Pexels API</td><td className="p-2">Retrieve relevant images</td><td className="p-2">Search keywords</td></tr>
                    </tbody>
                </table>

                <ul className="list-disc pl-5 text-sm mt-3">
                    <li><a href="https://openai.com/privacy" target="_blank" className="text-blue-700 underline">OpenAI Privacy Policy</a></li>
                    <li><a href="https://policies.google.com/privacy" target="_blank" className="text-blue-700 underline">Google Privacy Policy</a></li>
                    <li><a href="https://www.pexels.com/privacy-policy/" target="_blank" className="text-blue-700 underline">Pexels Privacy Policy</a></li>
                </ul>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">6. Ads</h2>
                <p>
                    Legsplorer displays advertisements to support the free use of the app. We use <strong>Google AdMob</strong> to show ads, which may include rewarded ads and banner ads.
                </p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Ads may use your device’s <strong>Advertising ID (GAID)</strong> to provide personalized ads and measure ad performance.</li>
                    <li>You can opt out of personalized ads by adjusting your device settings to reset or limit your Advertising ID.</li>
                    <li>We do not collect personal information ourselves for ad purposes; all ad data is handled according to <a href="https://policies.google.com/technologies/ads" target="_blank" className="text-blue-700 underline">Google AdMob policies</a>.</li>
                    <li>Watching ads is optional and does not affect your ability to use core features of the app.</li>
                </ul>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">7. Data Retention</h2>
                <p>We do not persist or retain request data on our servers beyond what is necessary to process and fulfill the request. Walk ratings stored in Supabase are retained only for calculating average scores.</p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">8. Location Data</h2>
                <p>Legsplorer does not collect or transmit live device location data. Coordinates or city names are used only to generate walks and are not retained.</p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">9. Children</h2>
                <p>Legsplorer is intended for a general audience and does not knowingly collect information from children under 13.</p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">10. Security</h2>
                <p>We use HTTPS encryption for all communications. Reasonable safeguards are applied to protect data during processing.</p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">11. Your Rights & Choices</h2>
                <p>You can remove all locally stored data by uninstalling Legsplorer or clearing the app’s data. The UUID stored for ratings is not personally identifiable. You can also opt out of personalized ads via your device settings.</p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">12. Changes to This Policy</h2>
                <p>We may update this Privacy Policy occasionally. The "Last updated" date at the top of this page will reflect any changes.</p>

                <h2 className="text-xl font-semibold text-blue-700 mt-6">13. Contact</h2>
                <p>If you have questions about this Privacy Policy or our data practices, please contact us at: <a href="mailto:info@huurhuishunter.com" className="text-blue-700 underline">info@huurhuishunter.com</a>.</p>

                <footer className="text-sm text-slate-500 mt-8">
                    This Privacy Policy was created for Legsplorer. Effective date: <strong>November 21, 2025</strong>.
                </footer>
            </div>
        </div>
    );
}
